import { G } from '@mobily/ts-belt';
import AccessTimeFilled from '@mui/icons-material/AccessTimeFilled';
import BadgeRounded from '@mui/icons-material/BadgeRounded';
import EuroOutlined from '@mui/icons-material/EuroOutlined';
import { Box, Typography } from '@mui/material';
import {
  RecordContextProvider,
  TextField,
  useGetOne,
  useRecordContext,
  useTranslate,
} from 'react-admin';

import { GetSavingsFundsRequestDetailsEndpoint } from '@Common/types/bo/b2b/savings_funds_requests';
import { GetBoExternalCompaniesPaymentMethodsBankDetailsEndpoint } from '@Common/types/bo_external/companies/payment_methods';

import { ShowFields, ShowSection } from '@sh4res/bo-common/components';
import { useCanAccess, useResourceBasePath } from '@sh4res/bo-common/hooks';

const testId = 'BoExternalB2bSavingsFundRequestsBeneficiaryDetailsShow';

export const BeneficiaryDetailsShow = () => {
  const record =
    useRecordContext<GetSavingsFundsRequestDetailsEndpoint['Client']['ResponseBody']>();
  const resourceBasePath = useResourceBasePath();
  const canShowBankDetails = useCanAccess({
    resource: `${resourceBasePath}companies/payment_methods/bank_details`,
    action: 'show',
  });
  const translate = useTranslate();

  const { error, data: bankDetails } = useGetOne<
    GetBoExternalCompaniesPaymentMethodsBankDetailsEndpoint['Client']['ResponseBody']
  >(
    `${resourceBasePath}companies/payment_methods/bank_details`,
    { id: record?.b2b_company_id as number },
    {
      enabled: G.isNotNullable(record?.b2b_company_id) && canShowBankDetails,
    },
  );

  if (!canShowBankDetails || error) {
    return (
      <Typography color="error" variant="subtitle1">
        {translate(
          'resources.bo_common.b2b.savings_funds_requests.payments.cant_show_bank_details',
        )}
      </Typography>
    );
  }

  return (
    <RecordContextProvider value={bankDetails}>
      <ShowFields<GetBoExternalCompaniesPaymentMethodsBankDetailsEndpoint['Client']['ResponseBody']>
        resource="bo_common.b2b.savings_funds_requests.payments"
        fields={{
          name: <TextField data-testid={`${testId}_NameField`} />,
          acc_iban: <TextField data-testid={`${testId}_IbanField`} />,
          acc_bic: <TextField data-testid={`${testId}_BicField`} />,
          reference: <TextField data-testid={`${testId}_ReferenceField`} />,
        }}
      />
    </RecordContextProvider>
  );
};

export const PaymentStep = () => {
  const translate = useTranslate();

  return (
    <Box>
      <ShowSection
        leftAligned
        label="resources.bo_common.b2b.savings_funds_requests.payments.use_beneficiary_details"
        noCard={true}>
        <BeneficiaryDetailsShow />
      </ShowSection>
      <ShowSection
        leftAligned
        label="resources.bo_common.b2b.savings_funds_requests.payments.important_information_title"
        noCard={true}>
        <Typography variant="body1" sx={{ color: 'grey', display: 'flex' }}>
          <BadgeRounded sx={{ mr: 2 }} />
          {translate(
            'resources.bo_common.b2b.savings_funds_requests.payments.important_information_body_1',
          )}
        </Typography>
        <Typography variant="body1" sx={{ color: 'grey', display: 'flex' }}>
          <EuroOutlined sx={{ mr: 2 }} />
          {translate(
            'resources.bo_common.b2b.savings_funds_requests.payments.important_information_body_2',
          )}
        </Typography>
        <Typography variant="body1" sx={{ color: 'grey', display: 'flex' }}>
          <AccessTimeFilled sx={{ mr: 2 }} />
          {translate(
            'resources.bo_common.b2b.savings_funds_requests.payments.important_information_body_3',
          )}
        </Typography>
      </ShowSection>
    </Box>
  );
};
